import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Utensils } from 'lucide-react';
import { FoodItem, FOOD_DATABASE, searchFoods } from '../data/foodDatabase';
import './FoodLog.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snacks';

interface FoodEntry {
  id: string;
  foodId: string;
  foodName: string;
  servings: number;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

interface DayLog {
  date: string;
  meals: Record<MealType, FoodEntry[]>;
}

interface CustomFood {
  name: string;
  calories: string;
  protein: string;
  carbs: string;
  fat: string;
  fiber: string;
  servingSize: string;
  servingUnit: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MEALS: { id: MealType; label: string; emoji: string }[] = [
  { id: 'breakfast', label: 'Breakfast', emoji: '🍳' },
  { id: 'lunch', label: 'Lunch', emoji: '🥗' },
  { id: 'dinner', label: 'Dinner', emoji: '🍽️' },
  { id: 'snacks', label: 'Snacks', emoji: '🍎' },
];

const DAILY_TARGETS = { calories: 2000, protein: 150, carbs: 250, fat: 65, fiber: 30 };

const emptyDay = (): DayLog['meals'] => ({
  breakfast: [], lunch: [], dinner: [], snacks: [],
});

const formatDate = (d: Date) => d.toISOString().split('T')[0];

// ─── Component ───────────────────────────────────────────────────────────────

const FoodLog: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [dayLog, setDayLog] = useState<DayLog>({ date: formatDate(new Date()), meals: emptyDay() });
  const [expandedMeals, setExpandedMeals] = useState<Record<MealType, boolean>>({
    breakfast: true, lunch: true, dinner: true, snacks: true,
  });

  // Add food modal
  const [addingToMeal, setAddingToMeal] = useState<MealType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [servings, setServings] = useState<number>(1);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  // Custom food
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customFood, setCustomFood] = useState<CustomFood>({
    name: '', calories: '', protein: '', carbs: '', fat: '', fiber: '', servingSize: '100', servingUnit: 'g',
  });

  // ── persistence ──────────────────────────────────────────────
  useEffect(() => {
    const key = `foodlog_${formatDate(currentDate)}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      setDayLog(JSON.parse(saved));
    } else {
      setDayLog({ date: formatDate(currentDate), meals: emptyDay() });
    }
  }, [currentDate]);

  const saveLog = (updated: DayLog) => {
    const key = `foodlog_${updated.date}`;
    localStorage.setItem(key, JSON.stringify(updated));
    setDayLog(updated);
  };

  // ── computed totals ───────────────────────────────────────────
  const totals = useMemo(() => {
    const all = Object.values(dayLog.meals).flat();
    return {
      calories: Math.round(all.reduce((s, e) => s + e.calories, 0)),
      protein: Math.round(all.reduce((s, e) => s + e.protein, 0)),
      carbs: Math.round(all.reduce((s, e) => s + e.carbs, 0)),
      fat: Math.round(all.reduce((s, e) => s + e.fat, 0)),
      fiber: Math.round(all.reduce((s, e) => s + e.fiber, 0)),
    };
  }, [dayLog]);

  const mealTotals = (meal: MealType) => {
    const entries = dayLog.meals[meal];
    return {
      calories: Math.round(entries.reduce((s, e) => s + e.calories, 0)),
      protein: Math.round(entries.reduce((s, e) => s + e.protein, 0)),
    };
  };

  // ── filters ───────────────────────────────────────────────────
  const filteredFoods = useMemo(() => {
    const results = searchFoods(searchQuery);
    if (activeCategory === 'All') return results;
    return results.filter(f => f.category === activeCategory);
  }, [searchQuery, activeCategory]);

  const categories = ['All', ...Array.from(new Set(FOOD_DATABASE.map(f => f.category)))];

  // ── actions ───────────────────────────────────────────────────
  const addEntry = () => {
    if (!selectedFood || !addingToMeal) return;
    const ratio = servings;
    const entry: FoodEntry = {
      id: `${Date.now()}`,
      foodId: selectedFood.id,
      foodName: selectedFood.name,
      servings,
      calories: Math.round(selectedFood.calories * ratio),
      protein: Math.round(selectedFood.protein * ratio * 10) / 10,
      carbs: Math.round(selectedFood.carbs * ratio * 10) / 10,
      fat: Math.round(selectedFood.fat * ratio * 10) / 10,
      fiber: Math.round(selectedFood.fiber * ratio * 10) / 10,
    };
    const updated: DayLog = {
      ...dayLog,
      meals: { ...dayLog.meals, [addingToMeal]: [...dayLog.meals[addingToMeal], entry] },
    };
    saveLog(updated);
    closeModal();
  };

  const addCustomEntry = () => {
    if (!customFood.name || !addingToMeal) return;
    const ratio = servings;
    const entry: FoodEntry = {
      id: `${Date.now()}`,
      foodId: 'custom',
      foodName: customFood.name,
      servings,
      calories: Math.round(parseFloat(customFood.calories || '0') * ratio),
      protein: Math.round(parseFloat(customFood.protein || '0') * ratio * 10) / 10,
      carbs: Math.round(parseFloat(customFood.carbs || '0') * ratio * 10) / 10,
      fat: Math.round(parseFloat(customFood.fat || '0') * ratio * 10) / 10,
      fiber: Math.round(parseFloat(customFood.fiber || '0') * ratio * 10) / 10,
    };
    const updated: DayLog = {
      ...dayLog,
      meals: { ...dayLog.meals, [addingToMeal]: [...dayLog.meals[addingToMeal], entry] },
    };
    saveLog(updated);
    closeModal();
  };

  const removeEntry = (meal: MealType, entryId: string) => {
    const updated: DayLog = {
      ...dayLog,
      meals: { ...dayLog.meals, [meal]: dayLog.meals[meal].filter(e => e.id !== entryId) },
    };
    saveLog(updated);
  };

  const closeModal = () => {
    setAddingToMeal(null);
    setSelectedFood(null);
    setSearchQuery('');
    setServings(1);
    setActiveCategory('All');
    setShowCustomForm(false);
    setCustomFood({ name: '', calories: '', protein: '', carbs: '', fat: '', fiber: '', servingSize: '100', servingUnit: 'g' });
  };

  const changeDate = (delta: number) => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + delta);
    setCurrentDate(d);
  };

  const isToday = formatDate(currentDate) === formatDate(new Date());

  // ── macro progress pct ────────────────────────────────────────
  const pct = (val: number, target: number) => Math.min(100, Math.round((val / target) * 100));

  // ── macro pie segments (SVG) ──────────────────────────────────
  const macroTotal = totals.protein * 4 + totals.carbs * 4 + totals.fat * 9;
  const pPct = macroTotal > 0 ? (totals.protein * 4) / macroTotal : 0;
  const cPct = macroTotal > 0 ? (totals.carbs * 4) / macroTotal : 0;
  const fPct = macroTotal > 0 ? (totals.fat * 9) / macroTotal : 0;

  const buildArc = (start: number, end: number) => {
    const toRad = (p: number) => p * 2 * Math.PI - Math.PI / 2;
    const r = 40;
    const cx = 50; const cy = 50;
    const x1 = cx + r * Math.cos(toRad(start));
    const y1 = cy + r * Math.sin(toRad(start));
    const x2 = cx + r * Math.cos(toRad(end));
    const y2 = cy + r * Math.sin(toRad(end));
    const large = end - start > 0.5 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
  };

  return (
    <div className="foodlog">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="foodlog-header">
        <div className="foodlog-title-row">
          <Utensils size={28} className="foodlog-icon" />
          <h1>Food Log</h1>
        </div>
        <div className="date-nav">
          <button className="date-btn" onClick={() => changeDate(-1)}><ChevronLeft size={18} /></button>
          <span className="date-label">
            {isToday ? 'Today — ' : ''}
            {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
          <button className="date-btn" onClick={() => changeDate(1)} disabled={isToday}><ChevronRight size={18} /></button>
        </div>
      </div>

      <div className="foodlog-body">
        {/* ── Daily Summary ──────────────────────────────────────── */}
        <div className="daily-summary">
          <div className="summary-header">
            <h2>Daily Summary</h2>
            <span className="calorie-badge">{totals.calories} / {DAILY_TARGETS.calories} kcal</span>
          </div>

          <div className="macro-bars">
            {[
              { key: 'protein', label: 'Protein', color: '#60a5fa', unit: 'g', val: totals.protein, target: DAILY_TARGETS.protein },
              { key: 'carbs', label: 'Carbs', color: '#34d399', unit: 'g', val: totals.carbs, target: DAILY_TARGETS.carbs },
              { key: 'fat', label: 'Fat', color: '#fbbf24', unit: 'g', val: totals.fat, target: DAILY_TARGETS.fat },
              { key: 'fiber', label: 'Fiber', color: '#a78bfa', unit: 'g', val: totals.fiber, target: DAILY_TARGETS.fiber },
            ].map(m => (
              <div key={m.key} className="macro-row">
                <div className="macro-meta">
                  <span className="macro-label" style={{ color: m.color }}>{m.label}</span>
                  <span className="macro-value">{m.val}{m.unit} / {m.target}{m.unit}</span>
                </div>
                <div className="macro-bar-track">
                  <div className="macro-bar-fill" style={{ width: `${pct(m.val, m.target)}%`, background: m.color }} />
                </div>
              </div>
            ))}
          </div>

          {/* Macro pie */}
          <div className="macro-pie-row">
            <svg width="100" height="100" viewBox="0 0 100 100" className="macro-pie">
              {macroTotal === 0 ? (
                <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.05)" />
              ) : (
                <>
                  <path d={buildArc(0, pPct)} fill="#60a5fa" />
                  <path d={buildArc(pPct, pPct + cPct)} fill="#34d399" />
                  <path d={buildArc(pPct + cPct, pPct + cPct + fPct)} fill="#fbbf24" />
                  <circle cx="50" cy="50" r="24" fill="var(--color-background)" />
                </>
              )}
              <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fill="var(--color-text)" fontSize="9" fontWeight="600">
                {macroTotal > 0 ? `${Math.round(macroTotal)} cal` : 'No data'}
              </text>
            </svg>
            <div className="pie-legend">
              <span className="legend-item"><span style={{ background: '#60a5fa' }} />Protein {Math.round(pPct * 100)}%</span>
              <span className="legend-item"><span style={{ background: '#34d399' }} />Carbs {Math.round(cPct * 100)}%</span>
              <span className="legend-item"><span style={{ background: '#fbbf24' }} />Fat {Math.round(fPct * 100)}%</span>
            </div>
          </div>
        </div>

        {/* ── Meals ─────────────────────────────────────────────── */}
        <div className="meals-panel">
          {MEALS.map(meal => {
            const isExpanded = expandedMeals[meal.id];
            const mt = mealTotals(meal.id);
            const entries = dayLog.meals[meal.id];
            return (
              <div key={meal.id} className="meal-card">
                <div className="meal-header" onClick={() => setExpandedMeals(prev => ({ ...prev, [meal.id]: !prev[meal.id] }))}>
                  <div className="meal-title">
                    <span className="meal-emoji">{meal.emoji}</span>
                    <span className="meal-name">{meal.label}</span>
                    <span className="meal-badge">{mt.calories} kcal · {mt.protein}g protein</span>
                  </div>
                  <div className="meal-actions">
                    <button className="add-food-btn" onClick={e => { e.stopPropagation(); setAddingToMeal(meal.id); }}>
                      <Plus size={14} /> Add Food
                    </button>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="meal-entries">
                    {entries.length === 0 ? (
                      <p className="empty-meal">No foods logged yet — tap "Add Food" to start!</p>
                    ) : (
                      <table className="entries-table">
                        <thead>
                          <tr>
                            <th>Food</th>
                            <th>Servings</th>
                            <th>Cal</th>
                            <th>P</th>
                            <th>C</th>
                            <th>F</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(entry => (
                            <tr key={entry.id}>
                              <td className="entry-name">{entry.foodName}</td>
                              <td>{entry.servings}</td>
                              <td className="cal-cell">{entry.calories}</td>
                              <td>{entry.protein}g</td>
                              <td>{entry.carbs}g</td>
                              <td>{entry.fat}g</td>
                              <td>
                                <button className="remove-btn" onClick={() => removeEntry(meal.id, entry.id)}>
                                  <Trash2 size={13} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Add Food Modal ─────────────────────────────────────── */}
      {addingToMeal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="food-modal" onClick={e => e.stopPropagation()}>
            <div className="food-modal-header">
              <h2>Add Food to {MEALS.find(m => m.id === addingToMeal)?.label}</h2>
              <button className="modal-close" onClick={closeModal}><X size={20} /></button>
            </div>

            {!showCustomForm ? (
              <>
                {/* Search */}
                <div className="food-search-bar">
                  <Search size={16} className="search-icon" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search foods…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="food-search-input"
                  />
                </div>

                {/* Category chips */}
                <div className="category-chips">
                  {categories.map(cat => (
                    <button key={cat} className={`chip ${activeCategory === cat ? 'active' : ''}`} onClick={() => setActiveCategory(cat)}>
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Food list */}
                <div className="food-list">
                  {filteredFoods.map(food => (
                    <div key={food.id} className={`food-item ${selectedFood?.id === food.id ? 'selected' : ''}`} onClick={() => setSelectedFood(food)}>
                      <div className="food-item-main">
                        <span className="food-item-name">{food.name}</span>
                        <span className="food-item-serving">Per {food.servingSize}{food.servingUnit}</span>
                      </div>
                      <div className="food-item-macros">
                        <span className="fi-cal">{food.calories} kcal</span>
                        <span className="fi-p">P: {food.protein}g</span>
                        <span className="fi-c">C: {food.carbs}g</span>
                        <span className="fi-f">F: {food.fat}g</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Servings picker */}
                {selectedFood && (
                  <div className="servings-row">
                    <label>Servings (1 = {selectedFood.servingSize}{selectedFood.servingUnit})</label>
                    <input
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={servings}
                      onChange={e => setServings(parseFloat(e.target.value) || 1)}
                      className="servings-input"
                    />
                    <span className="servings-preview">{Math.round(selectedFood.calories * servings)} kcal</span>
                  </div>
                )}

                <div className="food-modal-footer">
                  <button className="btn-outline" onClick={() => setShowCustomForm(true)}>
                    <Plus size={14} /> Custom Food
                  </button>
                  <button className="btn-primary-food" onClick={addEntry} disabled={!selectedFood}>
                    Add to {MEALS.find(m => m.id === addingToMeal)?.label}
                  </button>
                </div>
              </>
            ) : (
              /* Custom food form */
              <div className="custom-form">
                <h3>Custom Food Entry</h3>
                <div className="custom-grid">
                  <div className="cf-group full">
                    <label>Food Name*</label>
                    <input type="text" placeholder="e.g. Homemade Smoothie" value={customFood.name}
                      onChange={e => setCustomFood(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  {[
                    { key: 'calories', label: 'Calories (kcal)', placeholder: '200' },
                    { key: 'protein', label: 'Protein (g)', placeholder: '10' },
                    { key: 'carbs', label: 'Carbs (g)', placeholder: '30' },
                    { key: 'fat', label: 'Fat (g)', placeholder: '8' },
                    { key: 'fiber', label: 'Fiber (g)', placeholder: '3' },
                  ].map(field => (
                    <div key={field.key} className="cf-group">
                      <label>{field.label}</label>
                      <input type="number" placeholder={field.placeholder}
                        value={(customFood as any)[field.key]}
                        onChange={e => setCustomFood(p => ({ ...p, [field.key]: e.target.value }))} />
                    </div>
                  ))}
                </div>
                <div className="servings-row">
                  <label>Servings</label>
                  <input type="number" min="0.25" step="0.25" value={servings}
                    onChange={e => setServings(parseFloat(e.target.value) || 1)} className="servings-input" />
                </div>
                <div className="food-modal-footer">
                  <button className="btn-outline" onClick={() => setShowCustomForm(false)}>← Back</button>
                  <button className="btn-primary-food" onClick={addCustomEntry} disabled={!customFood.name}>
                    Add Custom Food
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodLog;
